// @ts-nocheck
import { record } from "rrweb";
import pako from "pako";
import { getUser, getUserAccessToken } from "../../utilities/setting";
import {
  USER_ROLES,
  BACKEND_API_BASE_URL,
  CLIENT_SOURCE,
  CLIENT_SOURCE_HEADER,
} from "../../utilities/constant";
import { API_SESSION_REPLAY_RECORDING_ME } from "../url";
import * as transport from "./transport";
import * as semanticCapture from "./semanticCapture";

const CHUNK_SEAL_INTERVAL_MS = 15000;
const CHECKOUT_EVERY_MS = 60000;
const SESSION_RESUME_WINDOW_MS = 30 * 60 * 1000;
const SS_SESSION_ID = "sr_session_id";
const SS_SESSION_STARTED = "sr_session_started_at";
const SS_SESSION_USER = "sr_session_user_id";

let recording = false;
let stopRrweb = null;
let chunkTimer = null;
let rrwebBuffer = [];
let bufferHasFullSnapshot = false;
let chunkIndex = 0;
let eventSeq = 0;
let currentScreen = "";

function isRecordableAgent() {
  try {
    const user = getUser();
    return Boolean(user?.agentData) && user?.role === USER_ROLES.CALL_CENTRE_AGENT;
  } catch {
    return false;
  }
}

async function isRecordingEnabledForAgent() {
  try {
    const token = getUserAccessToken();
    if (!token) return false;
    const res = await fetch(`${BACKEND_API_BASE_URL}${API_SESSION_REPLAY_RECORDING_ME}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
        [CLIENT_SOURCE_HEADER]: CLIENT_SOURCE,
      },
    });
    if (!res.ok) return false;
    const body = await res.json();
    return Boolean(body?.data?.enabled);
  } catch {
    return false;
  }
}

function gzipBase64(value) {
  const compressed = pako.gzip(JSON.stringify(value));
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < compressed.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, compressed.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function clearStoredSession() {
  try {
    sessionStorage.removeItem(SS_SESSION_ID);
    sessionStorage.removeItem(SS_SESSION_STARTED);
    sessionStorage.removeItem(SS_SESSION_USER);
  } catch {
  }
}

function resumeOrCreateSessionId() {
  const userId = String(getUser()?.id ?? "");
  try {
    const existing = sessionStorage.getItem(SS_SESSION_ID);
    const startedAt = Number(sessionStorage.getItem(SS_SESSION_STARTED));
    if (
      existing &&
      userId &&
      sessionStorage.getItem(SS_SESSION_USER) === userId &&
      Number.isFinite(startedAt) &&
      Date.now() - startedAt < SESSION_RESUME_WINDOW_MS
    ) {
      return { sessionId: existing, resumed: true, startedAt: new Date(startedAt) };
    }
  } catch {
    /* sessionStorage unavailable */
  }
  const sessionId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`.padEnd(36, "0");
  const startedAt = new Date();
  try {
    sessionStorage.setItem(SS_SESSION_ID, sessionId);
    sessionStorage.setItem(SS_SESSION_STARTED, String(startedAt.getTime()));
    sessionStorage.setItem(SS_SESSION_USER, userId);
  } catch {
    /* ignore */
  }
  return { sessionId, resumed: false, startedAt };
}

function sealChunk() {
  if (!rrwebBuffer.length) return;
  const events = rrwebBuffer;
  rrwebBuffer = [];
  const hasFullSnapshot = bufferHasFullSnapshot;
  bufferHasFullSnapshot = false;

  try {
    const payload = gzipBase64(events);
    transport.enqueueChunk({
      chunk_index: chunkIndex++,
      start_ts: new Date(events[0].timestamp).toISOString(),
      end_ts: new Date(events[events.length - 1].timestamp).toISOString(),
      event_count: events.length,
      has_full_snapshot: hasFullSnapshot ? 1 : 0,
      encoding: "gzip+base64",
      payload,
      size_bytes: payload.length,
    });
  } catch {
    /* compression failed; drop this chunk rather than crash the app */
  }
}

export function emitSemanticEvent(eventType, details = {}) {
  if (!recording) return;
  try {
    transport.enqueueEvent({
      ts: new Date().toISOString(),
      seq: eventSeq++,
      event_type: eventType,
      screen: details.screen || currentScreen,
      x: details.x ?? -1,
      y: details.y ?? -1,
      viewport_w: details.viewport_w || window.innerWidth || 0,
      viewport_h: details.viewport_h || window.innerHeight || 0,
      element_selector: details.element_selector || "",
      element_label: details.element_label || "",
      payload: details.payload ? JSON.stringify(details.payload) : "{}",
    });
  } catch {
    /* ignore */
  }
}

/** Public: the router gate reports screen changes here. */
export function setCurrentScreen(screenName, path) {
  if (!recording) {
    currentScreen = screenName || "";
    return;
  }
  if (screenName === currentScreen) return;
  currentScreen = screenName || "";
  emitSemanticEvent("screen_change", { payload: { path: path || "" } });
}

export async function startSessionRecording() {
  if (recording || typeof window === "undefined") return false;
  if (!isRecordableAgent()) return false;

  const enabled = await isRecordingEnabledForAgent();
  if (!enabled) return false;
  if (recording) return false;

  let { sessionId, resumed, startedAt } = resumeOrCreateSessionId();
  recording = true;
  eventSeq = 0;

  const device = {
    user_agent: navigator.userAgent,
    viewport_w: window.innerWidth,
    viewport_h: window.innerHeight,
    language: navigator.language,
  };
  let started = await transport.startSession(sessionId, {
    startedAt: startedAt.toISOString(),
    screen: currentScreen,
    device,
  });
  if (!started && resumed) {
    clearStoredSession();
    ({ sessionId, resumed, startedAt } = resumeOrCreateSessionId());
    started = await transport.startSession(sessionId, {
      startedAt: startedAt.toISOString(),
      screen: currentScreen,
      device,
    });
  }
  chunkIndex = resumed
    ? Math.ceil((Date.now() - startedAt.getTime()) / CHUNK_SEAL_INTERVAL_MS) + 2
    : 0;

  try {
    stopRrweb = record({
      emit(event, isCheckout) {
        if (isCheckout) {
          sealChunk();
          bufferHasFullSnapshot = true;
        }
        if (event.type === 2) bufferHasFullSnapshot = true; // FullSnapshot
        rrwebBuffer.push(event);
      },
      checkoutEveryNms: CHECKOUT_EVERY_MS,
      maskAllInputs: true,
      maskTextClass: "sr-mask",
      blockClass: "sr-block",
      slimDOMOptions: "all",
      inlineStylesheet: true,
      recordCanvas: false,
      sampling: {
        mousemove: 120,
        scroll: 200,
        media: 800,
        input: "last",
      },
    });
  } catch {
    stopRrweb = null;
  }

  semanticCapture.install((type, details) => emitSemanticEvent(type, details));

  chunkTimer = setInterval(sealChunk, CHUNK_SEAL_INTERVAL_MS);

  const exitFlush = () => {
    sealChunk();
    let userGone = true;
    try {
      userGone = !getUser();
    } catch {
      userGone = true;
    }
    if (userGone) {
      clearStoredSession();
      transport.endSession("beacon");
    } else {
      transport.flushOnExit();
    }
  };
  window.addEventListener("pagehide", exitFlush);
  window.addEventListener("beforeunload", exitFlush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") exitFlush();
  });

  return true;
}

export async function stopSessionRecording(reason = "explicit") {
  if (!recording) return;
  recording = false;

  try {
    stopRrweb?.();
  } catch {
    /* ignore */
  }
  stopRrweb = null;

  semanticCapture.uninstall();

  if (chunkTimer) {
    clearInterval(chunkTimer);
    chunkTimer = null;
  }

  sealChunk();
  clearStoredSession();
  await transport.endSession(reason);
}

export function isRecording() {
  return recording;
}

export { noteApiCall } from "./semanticCapture";
export { getActiveSessionId } from "./transport";
